(() => {
  if (customElements.get('cart-recommendations')) return;

  class CartRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      const configElement = this.querySelector('[data-recommendation-config]');
      if (!configElement) return;

      try {
        this.config = JSON.parse(configElement.textContent);
      } catch (error) {
        console.error('ZeonEra recommendations: invalid configuration.', error);
        return;
      }

      this.list = this.querySelector('[data-recommendation-list]');
      this.status = this.querySelector('[data-recommendation-status]');
      this.load();
    }

    normalizeId(value) {
      return String(value || '').split('/').pop();
    }

    normalizeColor(value) {
      const color = String(value || '').trim().toLowerCase();
      if (color === 'rose gold' || color === 'rose-gold' || color === 'rosegold') return 'Rose Gold';
      if (color === 'silver') return 'Silver';
      if (color === 'gold') return 'Gold';
      return '';
    }

    async load() {
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart.js`, {
          headers: { Accept: 'application/json' },
        });

        if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);

        const cart = await response.json();
        this.cart = cart;
        const recommendations = this.getRecommendations(cart);
        this.render(recommendations);
      } catch (error) {
        console.error('ZeonEra recommendations: unable to load.', error);
        this.hidden = true;
      }
    }

    getRecommendations(cart) {
      const settings = this.config.settings || {};
      const rules = Array.isArray(this.config.rules) ? this.config.rules : [];
      const products = Array.isArray(this.config.products) ? this.config.products : [];
      const productsById = new Map(products.map((product) => [this.normalizeId(product.id), product]));
      const rulesBySource = new Map(rules.map((rule) => [this.normalizeId(rule.sourceProductId), rule]));
      const cartProductIds = new Set();
      const colorCounts = { Gold: 0, Silver: 0, 'Rose Gold': 0 };

      for (const item of cart.items || []) {
        const productId = this.normalizeId(item.product_id);
        const product = productsById.get(productId);
        const variant = product?.variants?.find(
          (candidate) => this.normalizeId(candidate.id) === this.normalizeId(item.variant_id),
        );
        const color = this.normalizeColor(variant?.color);

        cartProductIds.add(productId);
        if (color) colorCounts[color] += Number(item.quantity) || 1;
      }

      const preferredColor = this.getPreferredColor(colorCounts, settings);
      const relevantCandidates = new Map();

      for (const sourceProductId of cartProductIds) {
        const rule = rulesBySource.get(sourceProductId);
        if (!rule) continue;

        (rule.candidateProductIds || []).forEach((candidateId, index) => {
          const productId = this.normalizeId(candidateId);
          if (!productId || cartProductIds.has(productId)) return;

          const product = productsById.get(productId);
          const variant = this.pickVariant(product, preferredColor);
          if (!product || !variant) return;

          const tier = index < 2 ? 0 : 1;
          const rank = index + 1;
          const existing = relevantCandidates.get(productId);

          if (existing) {
            existing.hits += 1;
            existing.tier = Math.min(existing.tier, tier);
            existing.rank = Math.min(existing.rank, rank);
            existing.colorScore = Math.max(existing.colorScore, this.colorScore(variant.color, preferredColor));
            return;
          }

          relevantCandidates.set(productId, {
            product,
            variant,
            tier,
            rank,
            hits: 1,
            colorScore: this.colorScore(variant.color, preferredColor),
            profitPriority: Number(rulesBySource.get(productId)?.profitPriority) || 0,
          });
        });
      }

      const relevant = [...relevantCandidates.values()].sort(
        (a, b) =>
          a.tier - b.tier ||
          a.rank - b.rank ||
          b.hits - a.hits ||
          b.colorScore - a.colorScore ||
          b.profitPriority - a.profitPriority,
      );

      const recommendationCount = Math.max(1, Number(settings.recommendationCount) || 3);
      const pairingSlots = Math.min(recommendationCount, Math.max(0, Number(settings.pairingSlots) || 2));
      const selected = relevant.slice(0, pairingSlots);
      const selectedProductIds = new Set(selected.map((entry) => this.normalizeId(entry.product.id)));

      const profitCandidates = rules
        .map((rule) => {
          const productId = this.normalizeId(rule.sourceProductId);
          const product = productsById.get(productId);
          const variant = this.pickVariant(product, preferredColor);
          const relevantEntry = relevantCandidates.get(productId);

          if (
            !product ||
            !variant ||
            cartProductIds.has(productId) ||
            selectedProductIds.has(productId)
          ) {
            return null;
          }

          return {
            product,
            variant,
            profitPriority: Number(rule.profitPriority) || 0,
            relevantTier: relevantEntry?.tier ?? 2,
            relevantRank: relevantEntry?.rank ?? 99,
            colorScore: this.colorScore(variant.color, preferredColor),
          };
        })
        .filter(Boolean)
        .sort(
          (a, b) =>
            b.profitPriority - a.profitPriority ||
            a.relevantTier - b.relevantTier ||
            a.relevantRank - b.relevantRank ||
            b.colorScore - a.colorScore,
        );

      for (const candidate of profitCandidates) {
        if (selected.length >= recommendationCount) break;
        const productId = this.normalizeId(candidate.product.id);
        if (selectedProductIds.has(productId)) continue;
        selected.push(candidate);
        selectedProductIds.add(productId);
      }

      if (selected.length < recommendationCount) {
        for (const candidate of relevant) {
          if (selected.length >= recommendationCount) break;
          const productId = this.normalizeId(candidate.product.id);
          if (selectedProductIds.has(productId)) continue;
          selected.push(candidate);
          selectedProductIds.add(productId);
        }
      }

      return selected.slice(0, recommendationCount);
    }

    getPreferredColor(counts, settings) {
      const roseAsGold = settings.roseGoldCountsAsGold !== false;
      const goldWeight = counts.Gold + (roseAsGold ? counts['Rose Gold'] : 0);
      const silverWeight = counts.Silver;

      if (
        counts['Rose Gold'] > 0 &&
        counts.Gold === 0 &&
        counts['Rose Gold'] > silverWeight
      ) {
        return 'Rose Gold';
      }

      if (goldWeight === silverWeight) return settings.goldWinsTies === false ? 'Silver' : 'Gold';
      return goldWeight > silverWeight ? 'Gold' : 'Silver';
    }

    colorScore(colorValue, preferredColor) {
      const color = this.normalizeColor(colorValue);
      if (color === preferredColor) return 3;
      if (preferredColor === 'Rose Gold' && color === 'Gold') return 2;
      if (preferredColor === 'Gold' && color === 'Rose Gold') return 2;
      return color ? 1 : 0;
    }

    pickVariant(product, preferredColor) {
      if (!product || !Array.isArray(product.variants)) return null;

      const available = product.variants.filter(
        (variant) => variant.available === true && variant.eligible === true,
      );
      if (!available.length) return null;

      const colorOrder =
        preferredColor === 'Silver'
          ? ['Silver', 'Gold', 'Rose Gold']
          : preferredColor === 'Rose Gold'
            ? ['Rose Gold', 'Gold', 'Silver']
            : ['Gold', 'Rose Gold', 'Silver'];

      for (const color of colorOrder) {
        const variant = available.find((candidate) => this.normalizeColor(candidate.color) === color);
        if (variant) return variant;
      }

      return available[0];
    }

    render(recommendations) {
      if (!this.list || !recommendations.length) {
        this.hidden = true;
        return;
      }

      const fragment = document.createDocumentFragment();
      recommendations.forEach((recommendation) => {
        fragment.appendChild(this.createCard(recommendation.product, recommendation.variant));
      });

      this.list.replaceChildren(fragment);
      this.hidden = false;
    }

    createCard(product, variant) {
      const settings = this.config.settings || {};
      const labels = settings.labels || {};
      const card = document.createElement('article');
      const productLink = document.createElement('a');
      const image = document.createElement('img');
      const content = document.createElement('div');
      const titleLink = document.createElement('a');
      const variantLabel = document.createElement('p');
      const price = document.createElement('div');
      const button = document.createElement('button');
      const imageUrl = variant.image || product.image;

      card.className = 'zeonera-cart-recommendation';
      productLink.className = 'zeonera-cart-recommendation__image-link';
      productLink.href = product.url;
      productLink.setAttribute(
        'aria-label',
        String(labels.viewProduct || 'View [product]').replace('[product]', product.title),
      );

      image.className = 'zeonera-cart-recommendation__image';
      image.src = imageUrl || '';
      image.alt = product.title;
      image.width = 120;
      image.height = 120;
      image.loading = 'lazy';
      productLink.appendChild(image);

      content.className = 'zeonera-cart-recommendation__content';
      titleLink.className = 'zeonera-cart-recommendation__title';
      titleLink.href = product.url;
      titleLink.textContent = product.title;

      variantLabel.className = 'zeonera-cart-recommendation__variant';
      variantLabel.textContent = this.normalizeColor(variant.color) || variant.title;

      price.className = 'zeonera-cart-recommendation__price';
      if (settings.bundleDiscountEnabled === true && variant.bundlePrice) {
        const regular = document.createElement('s');
        const bundle = document.createElement('strong');
        const saving = document.createElement('span');
        const regularLabel = String(labels.regularPrice || 'Regular price [price]').replace(
          '[price]',
          variant.priceFormatted,
        );
        const bundlePriceCents = this.bundlePriceInCents(variant.bundlePrice);
        const bundleFormatted =
          variant.bundlePriceFormatted || this.formatMoneyFromCents(bundlePriceCents, settings.currency);
        const savingFormatted = this.formatMoneyFromCents(
          Math.max(0, Number(variant.price) - bundlePriceCents),
          settings.currency,
        );

        regular.textContent = variant.priceFormatted;
        regular.setAttribute('aria-label', regularLabel);
        bundle.textContent = String(labels.bundlePrice || 'Bundle [price]').replace(
          '[price]',
          bundleFormatted,
        );
        saving.className = 'zeonera-cart-recommendation__saving';
        saving.textContent = String(labels.save || 'Save [price]').replace('[price]', savingFormatted);
        price.append(regular, bundle, saving);
      } else {
        price.textContent = variant.priceFormatted;
      }

      button.className = 'zeonera-cart-recommendation__add';
      button.type = 'button';
      button.textContent = labels.add || 'Add';
      button.dataset.variantId = this.normalizeId(variant.id);
      button.dataset.productId = this.normalizeId(product.id);
      button.addEventListener('click', () => this.addVariant(button));

      content.append(titleLink, variantLabel, price);
      card.append(productLink, content, button);
      return card;
    }

    bundlePriceInCents(value) {
      if (typeof value === 'number') return Math.round(value);
      if (typeof value === 'object' && value !== null) {
        return Math.round(Number(value.amount) * 100);
      }

      const parsed = Number(String(value).replace(/[^0-9.-]/g, ''));
      return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
    }

    formatMoneyFromCents(value, currency) {
      const amount = Number(value) / 100;
      if (!Number.isFinite(amount)) return '';

      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: currency || 'USD',
        }).format(amount);
      } catch (_error) {
        return `${amount.toFixed(2)} ${currency || ''}`.trim();
      }
    }

    async addVariant(button) {
      const labels = this.config.settings?.labels || {};
      const variantId = Number(button.dataset.variantId);
      const productId = this.normalizeId(button.dataset.productId);
      const sourceProductId = (this.cart?.items || [])
        .map((item) => this.normalizeId(item.product_id))
        .find((cartProductId) => cartProductId && cartProductId !== productId);
      if (!variantId || button.disabled) return;

      button.disabled = true;
      button.classList.add('is-loading');
      button.textContent = labels.adding || 'Adding...';

      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: 1,
                properties: sourceProductId
                  ? {
                      _zeonera_bundle_offer: 'true',
                      _zeonera_bundle_source: sourceProductId,
                    }
                  : {},
              },
            ],
            sections: ['cart-drawer', 'cart-icon-bubble'],
            sections_url: window.location.pathname,
          }),
        });
        const result = await response.json();

        if (!response.ok) throw new Error(result.description || 'Unable to add item');

        button.textContent = labels.added || 'Added';
        if (this.status) this.status.textContent = labels.added || 'Added';

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer?.renderContents) {
          cartDrawer.renderContents(result);
        } else {
          window.location.assign(`${root}cart`);
        }
      } catch (error) {
        console.error('ZeonEra recommendations: unable to add item.', error);
        button.disabled = false;
        button.classList.remove('is-loading');
        button.textContent = labels.add || 'Add';
        if (this.status) this.status.textContent = labels.error || "Couldn't add this item. Please try again.";
      }
    }
  }

  customElements.define('cart-recommendations', CartRecommendations);
})();
